import { ComponentProps, ForwardedRef, forwardRef, ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { faChevronDoubleLeft, faChevronDoubleRight, faChevronLeft, faChevronRight, faSearch } from '../../icons/regular'
import { Icon } from '../Icon/Icon'
import { Input, InputProps } from '../Input/Input'
import { Select } from '../Select/Select'
import { Button } from '../Button/Button'
import { Merge } from '../../util/types'
import { BaseEmptyView, BaseFilters, BasePagination, BaseSearch } from '../Table/Primitives'
import { NomColumnContext, useNomColumnContext, useNomTableContext } from './Context'

export interface CoreTablePaginationProps {
  className?: string | undefined
}
type TablePaginationProps = Merge<React.ComponentProps<'div'>, CoreTablePaginationProps>
export const TablePagination = forwardRef(
  (props: TablePaginationProps, ref: ForwardedRef<HTMLDivElement>) => {
    const { tableInstance } = useNomTableContext()
    const { previousPage, nextPage, getState, getCanPreviousPage, getCanNextPage, getPageCount, setPageIndex } = tableInstance
    const { pagination: { pageIndex } } = getState()

    if (getPageCount() <= 1) {
    // Don't show the pagination component if there are no other pages to show.
      return null
    }

    return (
      <BasePagination
        {...props}
        ref={ref}
        className={twMerge('core-pagination', props.className)}
      >
        <div className='typo-body-medium flex h-8 items-center'>
          <span className='mr-4'>
            {pageIndex + 1} of {getPageCount()}
          </span>
          <Button
            variant='plain'
            className='w-8'
            onClick={() => setPageIndex(0)}
            disabled={!getCanPreviousPage()}
          >
            <Icon icon={faChevronDoubleLeft} />
          </Button>
          <Button
            variant='plain'
            className='w-8'
            onClick={() => previousPage()}
            disabled={!getCanPreviousPage()}
          >
            <Icon icon={faChevronLeft} />
          </Button>
          <Button
            variant='plain'
            className='w-8'
            onClick={() => nextPage()}
            disabled={!getCanNextPage()}
          >
            <Icon icon={faChevronRight} />
          </Button>
          <Button
            variant='plain'
            className='w-8'
            onClick={() => setPageIndex(getPageCount() - 1)}
            disabled={!getCanNextPage()}
          >
            <Icon icon={faChevronDoubleRight} />
          </Button>
        </div>
      </BasePagination>
    )
  }
)

export const TableSearch = forwardRef(
  (props: InputProps, ref: ForwardedRef<HTMLInputElement>) => {
    const { tableInstance } = useNomTableContext()
    const { setGlobalFilter, getState } = tableInstance
    const { globalFilter } = getState()

    if (setGlobalFilter === undefined) {
    // eslint-disable-next-line no-console
      console.trace('[TableSearch] `useGlobalFilter` plugin not found. Did you add it to the `tableHooks` prop in <Table>? Skipping render...')
      return null
    }

    return (
      <BaseSearch>
        <Input
          {...props}
          ref={ref}
          icon={<Icon icon={faSearch} />}
          value={globalFilter}
          onChange={event => { setGlobalFilter(event.target.value) }}
        />
      </BaseSearch>
    )
  }
)

export const EmptyView = forwardRef(
  (props: ComponentProps<typeof BaseEmptyView>, ref: ForwardedRef<HTMLDivElement>) => {
    const { tableInstance } = useNomTableContext()

    if (tableInstance.getRowModel().rows.length > 0) {
      return null
    }

    return <BaseEmptyView {...props} ref={ref} />
  }
)

export const TableFilters = forwardRef(
  (props: ComponentProps<typeof BaseFilters>, ref: ForwardedRef<HTMLDivElement>) => {
    const { tableInstance } = useNomTableContext()

    const columnsWithFilter = tableInstance.getAllColumns()
      .filter(column => column.columnDef.meta?.filter)

    if (columnsWithFilter.length === 0) {
      return null
    }

    return (
      <BaseFilters
        {...props}
        ref={ref}
      >
        {columnsWithFilter.map(column => {
          // We checked this exists right before this map, TS doesn't understand us.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
          const Filter = column.columnDef.meta?.filter!
          return (
            <NomColumnContext.Provider
              key={column.id}
              value={column}
            >
              <Filter column={column} />
            </NomColumnContext.Provider>
          )
        })}
      </BaseFilters>
    )
  }
)

interface EnumFilterProps<D extends { label: ReactNode, value: string }> {
  label: ReactNode
  className?: string | undefined
  options: D[]
}
export const EnumFilter = forwardRef(
  <D extends { label: ReactNode, value: string }>(props: EnumFilterProps<D>, ref: ForwardedRef<HTMLDivElement>) => {
    const column = useNomColumnContext()

    return (
      <Select
        ref={ref}
        className={twMerge('min-w-[15rem]', props.className)}
        label={props.label}
        options={props.options}
        value={props.options.find(item => item.value === column.getFilterValue())}
        onChange={option => column.setFilterValue(option?.value)}
      />
    )
  }
)

export const MultiEnumFilter = forwardRef(
  <D extends { label: ReactNode, value: string }>(props: EnumFilterProps<D>, ref: ForwardedRef<HTMLDivElement>) => {
    const column = useNomColumnContext()

    return (
      <Select
        ref={ref}
        className={twMerge('min-w-[15rem]', props.className)}
        isMulti
        label={props.label}
        options={props.options}
        value={props.options.find(item => item.value === column.getFilterValue())}
        onChange={items => column.setFilterValue(items.map(item => item?.value))}
      />
    )
  }
)
interface BooleanFilterProps {
  label: ReactNode
  className?: string | undefined
}
export const BooleanFilter = forwardRef(
  (props: BooleanFilterProps, ref: ForwardedRef<HTMLDivElement>) => (
    <EnumFilter
      ref={ref}
      {...props}
      options={[
        {
          label: 'True',
          value: 'true'
        },
        {
          label: 'False',
          value: 'false'
        }
      ]}
    />
  )
)
